import React, { useEffect, useState } from "react";
import GoogleLoginButton from "./GoogleLoginButton";
import { useAuth } from "./AuthProvider";

function Login() {
  const { handleGoogleLogin } = useAuth();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [acceptTos, setAcceptTos] = useState(false);
  const [error, setError] = useState("");

  const features = [
    {
      title: "Smart Stock Screening",
      desc: "Pantau peluang saham BEI dengan tampilan data yang ringkas, cepat, dan mudah dipahami.",
    },
    {
      title: "Ask AI Insight",
      desc: "Dapatkan ringkasan analisa saham, risiko, dan poin penting dalam bahasa yang jelas.",
    },
    {
      title: "Watchlist & Portfolio",
      desc: "Lacak saham pilihan dan posisi portofolio Anda dalam satu dashboard yang rapi.",
    },
  ];

  const stats = [
    { label: "Focus", value: "BEI Stocks" },
    { label: "Experience", value: "Fast & Clean" },
    { label: "Style", value: "Simple AI UI" },
  ];

  useEffect(() => {
    if (!isLoginOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsLoginOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isLoginOpen]);

  const openLogin = () => {
    setError("");
    setIsLoginOpen(true);
  };

  const closeLogin = () => {
    setIsLoginOpen(false);
  };

  const onGoogleClick = async () => {
    if (!acceptTos) {
      setError(
        "Silakan centang persetujuan Syarat Layanan dan baca Pemberitahuan Privasi terlebih dahulu."
      );
      return;
    }

    try {
      if (typeof handleGoogleLogin === "function") {
        await handleGoogleLogin();
      }
    } catch (e) {
      setError(`Google Login Failed: ${e?.message || e}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {isLoginOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 px-4 backdrop-blur-md"
          onClick={closeLogin}
          aria-hidden="true"
        >
          <div
            className="relative w-full max-w-sm rounded-[32px] border border-white/10 bg-slate-900 p-8 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="login-title"
          >
            <button
              type="button"
              onClick={closeLogin}
              className="absolute right-6 top-6 text-slate-500 transition-colors hover:text-white"
              aria-label="Close login modal"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="mx-auto mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-500/20 ring-1 ring-indigo-400/30">
              <span className="text-xl font-bold text-indigo-300">SV</span>
            </div>

            <h2 id="login-title" className="mb-2 text-2xl font-bold text-white">
              Sign In
            </h2>
            <p className="mb-8 text-sm text-slate-400">
              Masuk untuk mulai memantau saham Anda
            </p>

            {error && (
              <div
                className="mb-6 rounded-xl border border-rose-500/20 bg-rose-500/10 p-3 text-center text-xs text-rose-400"
                role="alert"
                aria-live="polite"
              >
                {error}
              </div>
            )}

            <div className="space-y-3">
              <div
                className={`w-full rounded-2xl border border-white/10 p-1 transition ${
                  !acceptTos
                    ? "opacity-60"
                    : "hover:border-white/20 hover:bg-white/5"
                }`}
              >
                {acceptTos ? (
                  <GoogleLoginButton
                    onLoginSuccess={handleGoogleLogin}
                    onLoginFailure={(msg) =>
                      setError(`Google Login Failed: ${msg}`)
                    }
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      setError(
                        "Silakan centang persetujuan Syarat Layanan dan baca Pemberitahuan Privasi terlebih dahulu."
                      )
                    }
                    className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-slate-400 cursor-not-allowed"
                  >
                    Sign in with Google
                  </button>
                )}
              </div>
            
              {!acceptTos && (
                <p className="text-xs text-slate-500">
                  Centang persetujuan terlebih dahulu untuk mengaktifkan login.
                </p>
              )}
            </div>

            <div className="mt-8 text-left">
              <label className="group flex cursor-pointer items-start gap-3 text-sm text-slate-400">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 rounded-md border-white/10 bg-white/5 accent-indigo-500"
                  checked={acceptTos}
                  onChange={(e) => {
                    setAcceptTos(e.target.checked);
                    if (e.target.checked) setError("");
                  }}
                  aria-label="Saya setuju dengan Syarat Layanan dan telah membaca Pemberitahuan Privasi"
                />

                <span className="text-xs leading-tight transition-colors group-hover:text-slate-200">
                  Saya setuju dengan{" "}
                  <a
                    className="font-medium text-indigo-400 hover:underline"
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Syarat Layanan
                  </a>{" "}
                  dan telah membaca{" "}
                  <a
                    className="font-medium text-indigo-400 hover:underline"
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Pemberitahuan Privasi
                  </a>
                  .
                  <span className="mt-1 block text-slate-500">
                    Hanya nama & email Google yang digunakan untuk manajemen akun.
                  </span>
                </span>
              </label>
            </div>

            <p className="mt-8 text-[10px] font-semibold uppercase tracking-widest text-slate-600">
              Saham Vista Intelligent Platform
            </p>
          </div>
        </div>
      )}

      <header className="sticky top-0 z-30 border-b border-white/10 bg-slate-950/80 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-500/20 ring-1 ring-indigo-400/30">
              <span className="text-lg font-bold text-indigo-300">SV</span>
            </div>
            <div>
              <div className="text-base font-semibold tracking-wide">Saham Vista</div>
              <div className="text-xs text-slate-400">
                AI-powered stock insight for Indonesia
              </div>
            </div>
          </div>

          <nav className="hidden items-center gap-8 text-sm font-medium md:flex">
            <a href="#features" className="text-slate-300 transition hover:text-white">
              Features
            </a>
            <a href="#why" className="text-slate-300 transition hover:text-white">
              Why Saham Vista
            </a>
            <a href="#contact" className="text-slate-300 transition hover:text-white">
              Contact
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={openLogin}
              className="rounded-xl bg-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 transition hover:bg-indigo-400"
            >
              Sign In
            </button>
          </div>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(99,102,241,0.18),transparent_35%),radial-gradient(circle_at_80%_20%,rgba(59,130,246,0.12),transparent_28%)]" />
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-2 lg:px-8 lg:py-28">
            <div className="relative z-10">
              <div className="mb-5 inline-flex items-center rounded-full border border-indigo-400/20 bg-indigo-500/10 px-3 py-1 text-xs font-medium text-indigo-200">
                Built for Indonesian Stock Market
              </div>

              <h1 className="max-w-2xl text-4xl font-bold leading-tight tracking-tight text-white md:text-5xl lg:text-6xl">
                Simple, modern, and intelligent way to monitor saham BEI.
              </h1>

              <p className="mt-6 max-w-xl text-base leading-7 text-slate-300 md:text-lg">
                Saham Vista membantu Anda memantau watchlist, membaca insight penting, dan memahami saham dengan bantuan AI dalam antarmuka yang cepat dan profesional.
              </p>

              <div className="mt-8 flex flex-wrap gap-4">
                <button
                  type="button"
                  onClick={openLogin}
                  className="rounded-2xl bg-indigo-500 px-6 py-3 text-sm font-semibold text-white shadow-xl shadow-indigo-500/20 transition hover:bg-indigo-400"
                >
                  Start Exploring
                </button>
                <a
                  href="#features"
                  className="rounded-2xl border border-white/10 px-6 py-3 text-sm font-semibold text-slate-200 transition hover:border-white/20 hover:bg-white/5"
                >
                  View Features
                </a>
              </div>

              <div className="mt-10 grid max-w-lg grid-cols-1 gap-4 sm:grid-cols-3">
                {stats.map((item) => (
                  <div
                    key={item.label}
                    className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-sm backdrop-blur transition hover:bg-white/10"
                  >
                    <div className="text-lg font-semibold text-white">{item.value}</div>
                    <div className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-400">
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative z-10">
              <div className="rounded-[28px] border border-white/10 bg-white/5 p-4 shadow-2xl shadow-black/30 backdrop-blur-xl">
                <div className="rounded-[24px] border border-white/10 bg-slate-900/90 p-5">
                  <div className="flex items-center justify-between border-b border-white/10 pb-4">
                    <div>
                      <div className="text-sm text-slate-400">Dashboard Preview</div>
                      <div className="mt-1 text-xl font-semibold text-white">
                        Saham Vista Overview
                      </div>
                    </div>
                    <div className="rounded-xl bg-emerald-500/15 px-3 py-1 text-xs font-medium text-emerald-300 ring-1 ring-emerald-500/20">
                      Live Ready
                    </div>
                  </div>

                  <div className="mt-5 grid gap-4 md:grid-cols-2">
                    <div className="rounded-2xl border border-white/10 bg-slate-800/70 p-4">
                      <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                        Watchlist
                      </div>
                      <div className="mt-4 space-y-3">
                        {[
                          ["BBCA", "+2.4%"],
                          ["TLKM", "+1.1%"],
                          ["ASII", "-0.6%"],
                        ].map(([code, chg]) => (
                          <div
                            key={code}
                            className="flex items-center justify-between rounded-xl border border-white/5 bg-white/5 px-3 py-2"
                          >
                            <div>
                              <div className="font-semibold text-white">{code}</div>
                              <div className="text-[10px] text-slate-500">
                                AI summary available
                              </div>
                            </div>
                            <div
                              className={`text-sm font-bold ${
                                chg.startsWith("+")
                                  ? "text-emerald-400"
                                  : "text-rose-400"
                              }`}
                            >
                              {chg}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-slate-800/70 p-4">
                      <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                        Ask AI
                      </div>
                      <div className="mt-4 rounded-2xl bg-gradient-to-br from-indigo-500/15 to-blue-500/10 p-4 shadow-inner ring-1 ring-white/10">
                        <div className="flex items-center gap-2 text-sm font-semibold text-white">
                          <div className="h-1.5 w-1.5 rounded-full bg-indigo-400 animate-pulse" />
                          Ringkasan Cepat
                        </div>
                        <p className="mt-2 text-[13px] leading-relaxed text-slate-300">
                          Momentum masih positif, namun area resistance terdekat perlu diperhatikan. Cocok untuk pendekatan wait for confirmation.
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2 text-[10px] text-slate-300">
                          <span className="rounded-full bg-indigo-500/20 px-2.5 py-1 ring-1 ring-indigo-500/20">
                            Trend
                          </span>
                          <span className="rounded-full bg-white/10 px-2.5 py-1">
                            Risk Aware
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 rounded-2xl border border-white/10 bg-slate-800/70 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                          Portfolio Snapshot
                        </div>
                        <div className="mt-1 text-lg font-semibold text-white">
                          +8.42% this month
                        </div>
                      </div>
                      <button
                        type="button"
                        className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-slate-200 transition-colors hover:bg-white/10"
                      >
                        Open Portfolio
                      </button>
                    </div>
                    <div className="relative mt-4 h-24 overflow-hidden rounded-2xl border border-white/5 bg-gradient-to-r from-emerald-500/5 via-indigo-500/10 to-cyan-500/5" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="features" className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-sm font-semibold uppercase tracking-[0.25em] text-indigo-300">
              Core Features
            </div>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-white md:text-4xl">
              Designed to keep analysis focused and efficient.
            </h2>
            <p className="mt-4 text-slate-300">
              Tampilan yang ringan, insight yang jelas, dan pengalaman pengguna yang cocok untuk investor modern.
            </p>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {features.map((item) => (
              <div
                key={item.title}
                className="rounded-3xl border border-white/10 bg-white/5 p-8 shadow-lg shadow-black/20 transition hover:-translate-y-1 hover:border-indigo-500/30 hover:bg-white/[0.07]"
              >
                <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-500/15 ring-1 ring-indigo-400/20">
                  <div className="h-2 w-2 rounded-full bg-indigo-400" />
                </div>
                <h3 className="text-xl font-semibold text-white">{item.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-300">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section id="why" className="mx-auto max-w-7xl px-6 py-8 lg:px-8 lg:py-16">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="flex flex-col justify-center rounded-[32px] border border-white/10 bg-gradient-to-br from-indigo-500/10 to-slate-900 p-8">
              <div className="text-sm font-semibold uppercase tracking-[0.25em] text-indigo-300">
                Why Saham Vista
              </div>
              <h3 className="mt-3 text-2xl font-bold text-white md:text-3xl">
                Professional look, practical workflow.
              </h3>
              <p className="mt-4 max-w-xl text-sm leading-7 text-slate-400">
                Dibangun untuk membantu pengguna fokus pada hal yang penting: screening saham, membaca konteks, dan mengambil keputusan dengan lebih terstruktur.
              </p>
            </div>

            <div className="rounded-[32px] border border-white/10 bg-white/5 p-8">
              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  "Clean interface",
                  "Fast navigation",
                  "AI-assisted summary",
                  "Portfolio-ready",
                ].map((point) => (
                  <div
                    key={point}
                    className="rounded-2xl border border-white/10 bg-slate-900/70 p-5 text-sm font-medium text-slate-200 transition hover:bg-slate-800"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-1.5 w-1.5 rounded-full bg-indigo-400" />
                      {point}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
          <div className="relative overflow-hidden rounded-[40px] border border-white/10 bg-white/5 px-8 py-16 text-center shadow-2xl shadow-black/20">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.1),transparent_70%)]" />
            <div className="relative z-10">
              <div className="text-sm font-semibold uppercase tracking-[0.25em] text-indigo-300">
                Ready to Start
              </div>
              <h2 className="mt-4 text-3xl font-bold tracking-tight text-white md:text-5xl">
                Monitor saham with a cleaner, smarter experience.
              </h2>
              <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400">
                Mulai dari watchlist sederhana sampai insight berbasis AI, Saham Vista dibuat untuk pengalaman yang simple dan professional.
              </p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={openLogin}
                  className="rounded-2xl bg-indigo-500 px-10 py-4 text-base font-bold text-white shadow-lg shadow-indigo-500/20 transition hover:scale-[1.02] hover:bg-indigo-400"
                >
                  Create Account Free
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer id="contact" className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-12 text-sm text-slate-500 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-lg border border-indigo-400/20 bg-indigo-500/20">
              <span className="text-[10px] font-bold text-indigo-300">SV</span>
            </div>
            <span>© 2026 Saham Vista. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-8 font-medium">
            <a href="/privacy" className="transition-colors hover:text-indigo-400">
              Privacy Policy
            </a>
            <a href="/terms" className="transition-colors hover:text-indigo-400">
              Terms of Service
            </a>
            <button
              type="button"
              onClick={openLogin}
              className="transition-colors hover:text-indigo-400"
            >
              Contact Support
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Login;
